import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilAdviseWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CivilAdviseWizardDraft } from '../../../types/civilAdviseCaseRequest';
import * as civilAdviseService from '../../../services/civilAdviseCaseRequests';
import { WizardTempFileUploadList } from '../../../components/wizard/WizardTempFileUploadList';
import { sliceMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { CivilAdviseWizardActions } from './CivilAdviseWizardActions';
import { civilAdviseWizardPath, CivilAdviseWizardShell, FieldError, inpClass } from './CivilAdviseWizardShell';
import { useCivilAdviseLookups } from './useCivilAdviseLookups';
import { validateCivilAdviseStep } from './civilAdviseStepValidation';
import { useCaseCategorization } from './useCaseCategorization';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function CivilAdviseStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilAdviseWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useCivilAdviseLookups('step1', draft.provinceId);
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.hasConnectedMatters === 'yes' || draft.hasConnectedMatters === 'no') return;
    setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasConnectedMatters: 'no' }));
  }, [draft.hasConnectedMatters, setDraft]);

  useEffect(() => {
    if (draft.connectedAgdFileRef?.trim() && !draft.connectedAgdFileRefAvailable) {
      setDraft((d: CivilAdviseWizardDraft) => ({ ...d, connectedAgdFileRefAvailable: true }));
    }
  }, [draft.connectedAgdFileRef, draft.connectedAgdFileRefAvailable, setDraft]);


  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilAdviseStep(
      1,
      draft,
      cat.fileCategories,
      lookups.corporateInstitutes,
    );
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(civilAdviseWizardPath(2, draft.draftRequestId));
  };

  return (
    <CivilAdviseWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="civ-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d: CivilAdviseWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionAdviceSought}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.province}</label>
            <WizardSelect
              id="civ-province"
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={toOpts(lookups.provinces)}
              onChange={(id) => {
                setDraft((d: CivilAdviseWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '' }));
              }}
              onClear={() =>
                setDraft((d: CivilAdviseWizardDraft) => ({ ...d, provinceId: '', provinceName: '', districtId: '', districtName: '' }))
              }
            />
          </div>
          <div className="ccr-fg">
            <label>{f.district}</label>
            <WizardSelect
              id="civ-district"
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={toOpts(lookups.districts)}
              onChange={(id) => {
                setDraft((d: CivilAdviseWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id) }));
              }}
            />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="civ-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: CivilAdviseWizardDraft) => ({ ...d, yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) =>
                setDraft((d: CivilAdviseWizardDraft) => ({ ...d, otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))
              }
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fg" id="civ-previousAdvice" style={{ marginBottom: 14 }}>
          <label>
            {f.hasPreviousAdviceOnMatter} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="previousAdvice"
                checked={draft.hasPreviousAdvice === 'yes'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasPreviousAdvice: 'yes' }));
                  clearErr('hasPreviousAdvice');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="previousAdvice"
                checked={draft.hasPreviousAdvice === 'no'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasPreviousAdvice: 'no', previousAdviceFiles: [] }));
                  clearErr('hasPreviousAdvice');
                  clearErr('previousAdviceFiles');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.hasPreviousAdvice} />
        </div>

        {draft.hasPreviousAdvice === 'yes' ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>
              {f.previousAdviceAttachment} <span className="req">*</span>
            </label>
            <WizardTempFileUploadList
              files={draft.previousAdviceFiles}
              uploadFile={civilAdviseService.uploadCaseFile}
              error={errors.previousAdviceFiles}
              onClearError={() => clearErr('previousAdviceFiles')}
              onChange={(previousAdviceFiles) => setDraft((d: CivilAdviseWizardDraft) => ({ ...d, previousAdviceFiles }))}
              addButtonLabel={f.uploadDocuments}
            />
          </div>
        ) : null}

        <div className="ccr-fg" id="civ-connectedCase" style={{ marginBottom: 14 }}>
          <label>
            {f.connectedCaseRelevant} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedCaseRelevant"
                checked={draft.hasConnectedCaseRelevant === 'yes'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasConnectedCaseRelevant: 'yes' }));
                  clearErr('hasConnectedCaseRelevant');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedCaseRelevant"
                checked={draft.hasConnectedCaseRelevant === 'no'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasConnectedCaseRelevant: 'no', connectedCaseDetails: '' }));
                  clearErr('hasConnectedCaseRelevant');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.hasConnectedCaseRelevant} />
        </div>

        {draft.hasConnectedCaseRelevant === 'yes' ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>
              {f.connectedCaseDetails} <span className="req">*</span>
            </label>
            <textarea
              id="civ-connectedCaseDetails"
              className={inpClass(Boolean(errors.connectedCaseDetails))}
              rows={3}
              value={draft.connectedCaseDetails}
              onChange={(e) => {
                setDraft((d: CivilAdviseWizardDraft) => ({ ...d, connectedCaseDetails: maxLen('connectedCaseDetails', e.target.value, MAX_DESC) }));
              }}
              placeholder={f.includeConnectedCaseDetails}
            />
            <FieldError message={errors.connectedCaseDetails} />
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.caseNumber}</label>
          <input
            className={inpClass(Boolean(errors.caseNumber))}
            type="text"
            value={draft.caseNumber}
            onChange={(e) => setDraft((d: CivilAdviseWizardDraft) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }))}
            placeholder={f.courtReferenceNumber}
          />
          <FieldError message={errors.caseNumber} />
        </div>

        <div
          className="ccr-fg"
          id="civ-connectedMatters"
          style={{ marginBottom: draft.hasConnectedMatters === 'yes' ? 14 : 0 }}
        >
          <label>
            {f.connectedMatters} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedMatters"
                checked={draft.hasConnectedMatters === 'yes'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, hasConnectedMatters: 'yes' }));
                  clearErr('hasConnectedMatters');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedMatters"
                checked={draft.hasConnectedMatters === 'no'}
                onChange={() => {
                  setDraft((d: CivilAdviseWizardDraft) => ({
                    ...d,
                    hasConnectedMatters: 'no',
                    connectedCaseNumber: '',
                    connectedAgdFileRefAvailable: false,
                    connectedAgdFileRef: '' }));
                  clearErr('hasConnectedMatters');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.hasConnectedMatters} />
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>
                {f.connectedCaseNumber} <span className="req">*</span>
              </label>
              <input
                id="civ-connectedCaseNo"
                className={inpClass(Boolean(errors.connectedCaseNumber))}
                type="text"
                maxLength={MAX_REF}
                value={draft.connectedCaseNumber}
                onChange={(e) => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) }));
                  clearErr('connectedCaseNumber');
                }}
                placeholder={f.enterConnectedCaseNumber}
              />
              <FieldError message={errors.connectedCaseNumber} />
            </div>
            <div className="ccr-fg">
              <div className="ccr-agd-available-row">
                <label style={{ marginBottom: 0 }}>
                  {f.connectedAgdFileRef}
                  {draft.connectedAgdFileRefAvailable ? <span className="req"> *</span> : null}
                </label>
                <label className="ccr-check" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                  <input
                    id="civ-connectedAgdAvailable"
                    type="checkbox"
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d: CivilAdviseWizardDraft) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                      }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>{f.available}</span>
                </label>
              </div>
              <input
                id="civ-connectedAgd"
                className={inpClass(Boolean(errors.connectedAgdFileRef))}
                type="text"
                maxLength={MAX_REF}
                disabled={!draft.connectedAgdFileRefAvailable}
                style={!draft.connectedAgdFileRefAvailable ? { cursor: 'default', pointerEvents: 'none' } : undefined}
                value={draft.connectedAgdFileRef}
                onChange={(e) => {
                  setDraft((d: CivilAdviseWizardDraft) => ({ ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) }));
                  clearErr('connectedAgdFileRef');
                }}
                placeholder={f.enterConnectedAgdFileRef}
              />
              <div className="ccr-slbl">{f.connectedAgdHint}</div>
              <FieldError message={errors.connectedAgdFileRef} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <CivilAdviseWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </CivilAdviseWizardShell>
  );
}
